import { Vector3 } from 'three';
import { Mesh, MeshProperties, MeshFields } from '../../core/Mesh';
import { Cloud, Clouds } from '@react-three/drei';
import * as THREE from 'three';
import { forwardRef, useMemo } from 'react';
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface CloudMeshProperties extends MeshProperties {
  segments: number;
  bounds: Vector3;
  volume: number;
  fade: number;
  speed: number;
  opacity: number;
  growth: number;
  seed: number;
  color: string;
  materialId?: string;
}

export interface CloudMesh extends Mesh<CloudMeshProperties> { 
  subtype: 'cloud';
  properties: CloudMeshProperties;
}

// Create a reusable Cloud component that can be used in both preview and world
const CloudComponent = forwardRef<THREE.Group, { properties: CloudMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { transform, segments, bounds, volume, fade, speed, opacity, growth, seed, color, materialId } = properties;
  const { activeWorld } = useAppContext();

  // In preview mode, use simplified transform
  const position = isPreview ? [0, 0, 0] : [transform.position.x, transform.position.y, transform.position.z];
  const rotation = isPreview ? [0, 0, 0] : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale = isPreview ? [1, 1, 1] : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshBasicMaterial({ color });
  }, [material, material?.properties, isPreview, color]);

  return (
    <group
      ref={ref}
      position={position as [number, number, number]}
      rotation={rotation as [number, number, number]}
      scale={scale as [number, number, number]}
    >
      <Clouds material={meshMaterial}>
        <Cloud
          segments={segments}
          bounds={[bounds.x, bounds.y, bounds.z]}
          volume={volume}
          fade={fade}
          speed={speed}
          opacity={opacity}
          growth={growth}
          seed={seed}
          color={color}
        />
      </Clouds>
    </group>
  );
});

export const CloudMeshItem: CloudMesh = {
  id: '',
  name: 'Cloud',
  type: 'mesh',
  subtype: 'cloud',
  icon: 'Cloud',
  properties: {
    segments: 20,
    bounds: new Vector3(5, 1, 1),
    volume: 6,
    fade: 10,
    speed: 0,
    opacity: 1,
    growth: 4,
    seed: Math.random(),
    color: '#ffffff',
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new THREE.Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.segments',
      type: 'number',
      label: 'Segments',
      default: 20,
      panel: 'geometry'
    },
    {
      key: 'properties.bounds',
      type: 'vector3',
      label: 'Bounds',
      default: new Vector3(5, 1, 1),
      panel: 'geometry'
    },
    {
      key: 'properties.volume',
      type: 'number',
      label: 'Volume',
      default: 6,
      panel: 'geometry'
    },
    {
      key: 'properties.fade',
      type: 'number',
      label: 'Fade',
      default: 10,
      panel: 'geometry'
    },
    {
      key: 'properties.speed',
      type: 'number',
      label: 'Speed',
      default: 0,
      panel: 'geometry'
    },
    {
      key: 'properties.opacity',
      type: 'number',
      label: 'Opacity',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.growth',
      type: 'number',
      label: 'Growth',
      default: 4,
      panel: 'geometry'
    },
    {
      key: 'properties.color',
      type: 'string',
      label: 'Color',
      default: '#ffffff',
      panel: 'geometry'
    }
  ],
  render: (properties: CloudMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <CloudComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Group>} />;
  }
}; 